import {
  importCounterparties,
  parseCounterpartiesForImport,
} from "@/apis/custom/customRoutes";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSession } from "@/lib/auth/auth";
import { cn } from "@/lib/utils";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { FileText, Loader2, Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type ParsingResponse = Awaited<ReturnType<typeof parseCounterpartiesForImport>>;

export function ImportCounterpartiesModal({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { data: session } = useSession();

  const [fileMetadata, setFileMetadata] = useState<{
    name: string;
    size: number;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [parsingState, setParsingState] = useState<
    | { status: "idle" }
    | { status: "parsing" }
    | {
        status: "success";
        results: ParsingResponse;
      }
    | { status: "error"; message: string }
  >({ status: "idle" });

  useEffect(() => {
    if (open) {
      setFileMetadata(null);
      setParsingState({ status: "idle" });
      setIsSubmitting(false);
      setDragActive(false);
    }
  }, [open]);

  const handleFileChange = (file: File) => {
    setFileMetadata({
      name: file.name,
      size: file.size,
    });
    parseFile(file);
  };

  const parseFile = async (fileToParse: File) => {
    setParsingState({ status: "parsing" });
    try {
      const results = await parseCounterpartiesForImport(fileToParse);

      setParsingState({ status: "success", results });
    } catch (error) {
      setParsingState({
        status: "error",
        message: t("suppliers.import.parsing_error_message", {
          filename: fileToParse.name,
        }),
      });

      setFileMetadata(null);
      const fileInput = document.getElementById(
        "counterparties-file-input",
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files?.[0]) {
      handleFileChange(e.target.files[0]);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFileMetadata(null);
    setParsingState({ status: "idle" });

    const fileInput = document.getElementById(
      "counterparties-file-input",
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i];
  };

  const onSubmit = async () => {
    if (parsingState.status !== "success") {
      return;
    }

    const { newCreditors, newDebtors } = parsingState.results;

    const totalNew = newCreditors.length + newDebtors.length;

    if (totalNew === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await importCounterparties({
        creditors: newCreditors,
        debtors: newDebtors,
      });

      toast.success(t("suppliers.import.success", { new: totalNew }));
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to import counterparties:", error);
      toast.error(t("suppliers.import.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalNew =
    parsingState.status === "success"
      ? parsingState.results.newCreditors.length +
        parsingState.results.newDebtors.length
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("suppliers.import.title")}</DialogTitle>
          <DialogDescription className="hidden" />
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t("suppliers.import.info_text")}
          </p>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {t("mandanten_create.step2.datev_steps")}
            </p>

            <img
              src="/datev_screenshot_debtors_creditors.png"
              alt={t(
                "mandanten_create.step2.datev_debtors_creditors_export_screenshot",
              )}
              width={900}
              height={600}
              className="rounded-lg border"
            />

            <p className="text-sm text-gray-600">
              <span className="mr-2">
                {t("mandanten_create.step2.more_info")}
              </span>
              <a
                href="https://tabula-accounting.notion.site/Gesch-ftspartner-aus-DATEV-exportieren-276eecb29d0480b4bfa7f8ea9fbc87c9"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {t("mandanten_create.step2.guide")}
              </a>
            </p>
          </div>

          <div className="space-y-2">
            {!fileMetadata ? (
              <>
                <div
                  className={cn(
                    "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer",
                    dragActive
                      ? "border-primary bg-primary/5"
                      : "border-gray-300",
                    "hover:border-primary transition-colors",
                  )}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleChange}
                    className="hidden"
                    id="counterparties-file-input"
                  />
                  <label
                    htmlFor="counterparties-file-input"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="h-8 w-8 mb-3 text-gray-500" />
                    <span className="text-sm font-medium mb-1">
                      {t("suppliers.import.click_or_drag")}
                    </span>
                    <span className="text-xs text-gray-500">
                      {t("suppliers.import.file_type")}
                    </span>
                  </label>
                </div>

                {parsingState.status === "error" && (
                  <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-red-800 mb-1">
                          {t("suppliers.import.parsing_error_title")}
                        </h4>
                        <p className="text-sm text-red-700">
                          {parsingState.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gray-100 p-2 rounded">
                        <FileText className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {fileMetadata.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(fileMetadata.size)}
                        </p>
                        {parsingState.status === "parsing" && (
                          <div className="flex items-center space-x-2 mt-1">
                            <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                            <span className="text-xs text-blue-500">
                              {t("suppliers.import.parsing")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFile}
                      disabled={
                        parsingState.status === "parsing" || isSubmitting
                      }
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {parsingState.status === "success" && (
                    <div className="mt-3 flex flex-col gap-1">
                      <span
                        className={cn(
                          "text-sm font-medium",
                          parsingState.results.newCreditors.length === 0
                            ? "text-yellow-600"
                            : "text-green-600",
                        )}
                      >
                        {t("creditors.import.results", {
                          new: parsingState.results.newCreditors.length,
                          existing: parsingState.results.existingCreditorsCount,
                        })}
                      </span>
                      <span
                        className={cn(
                          "text-sm font-medium",
                          parsingState.results.newDebtors.length === 0
                            ? "text-yellow-600"
                            : "text-green-600",
                        )}
                      >
                        {t("debtors.import.results", {
                          new: parsingState.results.newDebtors.length,
                          existing: parsingState.results.existingDebtorsCount,
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {parsingState.status === "success" &&
                  parsingState.results.newCreditors.length === 0 && (
                    <NoNewCounterpartiesWarning type={"creditor"} />
                  )}

                {parsingState.status === "success" &&
                  parsingState.results.newDebtors.length === 0 && (
                    <NoNewCounterpartiesWarning type={"debtor"} />
                  )}
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting || parsingState.status === "parsing"}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={
              parsingState.status !== "success" ||
              isSubmitting ||
              totalNew === 0
            }
            className="gap-3"
          >
            {isSubmitting
              ? t("suppliers.import.parsing")
              : t("suppliers.import.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoNewCounterpartiesWarning({ type }: { type: "debtor" | "creditor" }) {
  const { t } = useTranslation();

  const { title, description } = (
    {
      debtor: {
        title: "debtors.import.no_new_warning",
        description: "debtors.import.no_new_warning_description",
      },
      creditor: {
        title: "creditors.import.no_new_warning",
        description: "creditors.import.no_new_warning_description",
      },
    } as const
  )[type];

  return (
    <div className="mt-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-orange-800 mb-1">
            {t(title)}
          </h4>
          <p className="text-sm text-orange-700">{t(description)}</p>
        </div>
      </div>
    </div>
  );
}
